import React, { useState, useEffect, useRef } from "react";
import { FiX, FiImage, FiGlobe } from "react-icons/fi";
import toast from "react-hot-toast";
import PagePickerModal from "./PagePickerModal";
import CountryPicker from "./CountryPicker";
import { fetchTaskEvents } from "../../../utils/taskApi";
import { countryName } from "../../../utils/countries";
import {
  TASK_IMAGE_ACCEPT,
  TASK_IMAGE_HINT,
  taskImageFileError,
  taskImageFromClipboard,
} from "../../../utils/taskImage";

const TASK_TYPES = [
  { value: "website_visit", label: "Website Visit" },
  { value: "download_app", label: "Download App" },
  { value: "telegram", label: "Telegram" },
  { value: "youtube", label: "YouTube" },
];

const TASK_PLATFORMS = [
  { value: "both", label: "Both (Android & iOS)" },
  { value: "android", label: "Android only" },
  { value: "ios", label: "iOS only" },
];

/**
 * What the description box starts out saying, per task type. It is filled in
 * for the admin to read and edit — not applied behind their back — so
 * whatever ends up in the box is exactly what gets saved.
 */
const DEFAULT_DESCRIPTION = {
  download_app:
    "Install the app from the official store and open it once. Complete the task to receive your event points.",
  telegram:
    "Join the official Telegram channel using the provided link. Stay connected to receive your event points.",
  youtube:
    "Watch the provided YouTube video for the required duration. Complete the task to get event points.",
  website_visit:
    "Visit the provided website and explore the required page. Stay for the required time to complete the task and receive points.",
};

/**
 * Only a box the admin has not written in themselves gets refilled when the
 * type changes — empty, or still holding another type's default verbatim.
 * The moment they edit a word of it, it is theirs and switching type leaves
 * it alone.
 */
const isUntouchedDescription = (text) => {
  const value = (text || "").trim();
  if (!value) return true;
  return Object.values(DEFAULT_DESCRIPTION).some((preset) => preset === value);
};

/**
 * One control instead of two.
 *
 * The API carries `status` (what the scheduler reads) and `isActive` (the
 * older switch) and keeps them in step. Showing both to an admin invited the
 * question "what is the difference?" — to which the honest answer is "nothing
 * you need to care about". So the form asks the question they actually have,
 * and sets both fields from the answer.
 */
const LIFECYCLE = [
  {
    key: "live",
    label: "Live",
    hint: "In the daily rotation",
    values: { status: "ready", is_active: true },
  },
  {
    key: "draft",
    label: "Draft",
    hint: "Saved, not published yet",
    values: { status: "draft", is_active: false },
  },
  {
    key: "paused",
    label: "Paused",
    hint: "Kept, but switched off",
    values: { status: "paused", is_active: false },
  },
  {
    key: "archived",
    label: "Archived",
    hint: "Retired for good",
    values: { status: "archived", is_active: false },
  },
];

/**
 * How often a task may come back, as an admin would say it.
 *
 * "Once only" is first because it is what an empty cooldown means, and it is
 * the answer most likely to be chosen by accident when the field is a bare
 * number box.
 */
const REPEAT_PRESETS = [
  { label: "Once only", value: "" },
  { label: "Every day", value: 1 },
  { label: "Every 3 days", value: 3 },
  { label: "Every week", value: 7 },
  { label: "Every month", value: 30 },
];

/** Weight is a number the scheduler sorts by; nobody thinks in 250s. */
const PRIORITIES = [
  { label: "Low", value: 50, hint: "Fills gaps" },
  { label: "Normal", value: 100, hint: "Most tasks" },
  { label: "High", value: 200, hint: "Comes up first" },
];

const emptyForm = {
  // Left undefined rather than null: the API reads "no event_id sent" as "the
  // event running now", which is what an admin adding a task mid-event means.
  event_id: "",
  title: "",
  description: DEFAULT_DESCRIPTION.website_visit,
  task_type: "website_visit",
  platform: "both",
  // ISO codes. Empty is global — every user, in every country.
  countries: [],
  link: "",
  android_link: "",
  ios_link: "",
  video_url: "",
  reward_points: "",
  visit_duration: "",
  is_active: true,

  // Scheduling. A new task defaults to Ready with a 7-day cooldown rather
  // than to the API's own defaults, because the alternative — no cooldown —
  // means "run once, ever", which is almost never what someone adding a task
  // to a daily rotation intends.
  status: "ready",
  weight: 100,
  cooldown_days: 7,
  valid_from: "",
  valid_until: "",
  max_completions: "",
};

/** Placeholder for the main link, per type — a generic one teaches nothing. */
const LINK_PLACEHOLDER = {
  telegram: "https://t.me/yourchannel",
  youtube: "https://youtube.com/watch?v=...",
  download_app: "Store URL — used when the platform links below are blank",
  website_visit: "https://example.com/page",
};

const LINK_LABEL = {
  telegram: "Telegram Link",
  youtube: "Channel or Video Link",
  download_app: "Store Link",
  website_visit: "Website Link",
};

/**
 * A numbered step, so the form reads as a sequence rather than a wall of
 * inputs — what the task is, where it sends people, what it pays, when it
 * runs.
 */
const Section = ({ step, title, hint, children }) => (
  <section className="pt-5 first:pt-0 border-t first:border-t-0 border-gray-100">
    <div className="flex items-start gap-2.5 mb-3">
      <span className="w-5 h-5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
        {step}
      </span>
      <div>
        <h3 className="text-xs font-bold text-gray-800 leading-none">{title}</h3>
        {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
      </div>
    </div>
    <div className="space-y-4 pl-7">{children}</div>
  </section>
);

/** A labelled choice button — the pattern the whole "when it runs" step uses. */
const Choice = ({ selected, label, hint, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`flex-1 min-w-[104px] text-left px-3 py-2 rounded-xl border transition ${
      selected
        ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500/20"
        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
    }`}
  >
    <span className={`block text-xs font-semibold ${selected ? "text-blue-700" : "text-gray-700"}`}>
      {label}
    </span>
    {hint && <span className="block text-[10px] text-gray-400 mt-0.5 leading-tight">{hint}</span>}
  </button>
);

const FIELD_CLASS =
  "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500/20";

const TaskModal = ({ isOpen, onClose, onSave, editingTask = null }) => {
  const isEditing = Boolean(editingTask);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [dragOver, setDragOver] = useState(false);
  // The fields most tasks never touch, folded away so the common case is two
  // questions rather than six.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [events, setEvents] = useState([]);
  // "loading" | "ready" | "failed" — an empty dropdown with no explanation is
  // indistinguishable from a broken one, which is exactly how this first
  // shipped. The state is tracked so the field can say which it is.
  const [eventsState, setEventsState] = useState("loading");
  const [eventsError, setEventsError] = useState(null);
  const [eventsAttempt, setEventsAttempt] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setEventsState("loading");

    fetchTaskEvents()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setEvents(list);
        setEventsState("ready");

        // A new task goes into the running event, so select it outright rather
        // than describing it in a placeholder. Naming it in *both* the
        // placeholder and the list made one event look like two — identical to
        // read, different in what they send.
        const live = list.find((event) => event.status === "running");
        if (live && !editingTask) {
          setForm((prev) => (prev.event_id === "" ? { ...prev, event_id: String(live.id) } : prev));
        }
      })
      .catch((err) => {
        // Saving still works — an omitted event_id lands on the running event
        // — but the admin is told *what* went wrong, not merely that something
        // did. "Not deployed" and "deployed but erroring" need different
        // people to do different things.
        if (cancelled) return;
        setEvents([]);
        setEventsError(
          err.status === 404
            ? "This API route is missing from the deployed backend — the server is running an older build."
            : err.status >= 500
            ? `The server errored (HTTP ${err.status}). A pending database migration is the usual cause.`
            : err.message || "Unknown error"
        );
        setEventsState("failed");
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, eventsAttempt]);

  const runningEvent = events.find((event) => event.status === "running") || null;
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editingTask) {
      setForm({
        event_id: editingTask.event_id == null ? "" : String(editingTask.event_id),
        title: editingTask.title || "",
        description: editingTask.description || "",
        task_type: editingTask.task_type || "website_visit",
        platform: editingTask.platform || "both",
        countries: Array.isArray(editingTask.countries) ? editingTask.countries : [],
        link: editingTask.link || "",
        android_link: editingTask.android_link || "",
        ios_link: editingTask.ios_link || "",
        video_url: editingTask.video_url || "",
        reward_points: editingTask.reward_points ?? "",
        visit_duration: editingTask.visit_duration ?? "",
        is_active: editingTask.is_active ?? true,
        status: editingTask.status || "ready",
        weight: editingTask.weight ?? 100,
        // An existing task with no cooldown really has none — do not quietly
        // impose the create-time default on something already saved.
        cooldown_days: editingTask.cooldown_days ?? "",
        valid_from: editingTask.valid_from || "",
        valid_until: editingTask.valid_until || "",
        max_completions: editingTask.max_completions ?? "",
      });
      setPreviewUrl(editingTask.image || "");
    } else {
      setForm(emptyForm);
      setPreviewUrl("");
    }
    setImageFile(null);
    setShowAdvanced(
      Boolean(editingTask && (editingTask.max_completions || editingTask.valid_from || editingTask.valid_until))
    );
  }, [editingTask, isOpen]);

  if (!isOpen) return null;

  const handleTaskTypeChange = (task_type) => {
    setForm((prev) => ({
      ...prev,
      task_type,
      description: isUntouchedDescription(prev.description)
        ? DEFAULT_DESCRIPTION[task_type] || ""
        : prev.description,
    }));
  };

  const acceptImage = (file) => {
    const error = taskImageFileError(file);
    if (error) {
      toast.error(error);
      return false;
    }
    setImageFile(file);
    if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!acceptImage(file)) e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) acceptImage(file);
  };

  const handlePaste = (e) => {
    const image = taskImageFromClipboard(e);
    if (!image) return;
    e.preventDefault();
    if (acceptImage(image)) toast.success("Image pasted from clipboard");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }

    setLoading(true);
    try {
      const rewardPoints = form.reward_points === "" ? 0 : Number(form.reward_points);
      const visitDuration = form.visit_duration === "" ? null : Number(form.visit_duration);
      const cooldownDays = form.cooldown_days === "" ? null : Number(form.cooldown_days);
      const maxCompletions = form.max_completions === "" ? null : Number(form.max_completions);

      if (form.valid_from && form.valid_until && form.valid_from > form.valid_until) {
        toast.error("The campaign window ends before it starts");
        setLoading(false);
        return;
      }

      let payload;
      let isMultipart = false;
      if (imageFile) {
        payload = new FormData();
        if (form.event_id !== "") payload.append("event_id", form.event_id);
        payload.append("title", form.title.trim());
        payload.append("description", form.description.trim());
        payload.append("task_type", form.task_type);
        payload.append("platform", form.platform);
        // multipart carries text, so the list travels as JSON; `[]` is global.
        payload.append("countries", JSON.stringify(form.countries));
        payload.append("link", form.link.trim());
        payload.append("android_link", form.android_link.trim());
        payload.append("ios_link", form.ios_link.trim());
        payload.append("video_url", form.video_url.trim());
        payload.append("reward_points", rewardPoints);
        if (visitDuration !== null) payload.append("visit_duration", visitDuration);
        payload.append("is_active", form.is_active ? "true" : "false");
        payload.append("status", form.status);
        payload.append("weight", form.weight === "" ? 100 : Number(form.weight));
        // multipart carries strings, and an empty one is how "no value" is
        // spelled here — the API reads it back as null.
        if (cooldownDays !== null) payload.append("cooldown_days", cooldownDays);
        if (form.valid_from) payload.append("valid_from", form.valid_from);
        if (form.valid_until) payload.append("valid_until", form.valid_until);
        if (maxCompletions !== null) payload.append("max_completions", maxCompletions);
        payload.append("image", imageFile);
        isMultipart = true;
      } else {
        payload = {
          ...(form.event_id === "" ? {} : { event_id: Number(form.event_id) }),
          title: form.title.trim(),
          description: form.description.trim(),
          task_type: form.task_type,
          platform: form.platform,
          countries: form.countries,
          link: form.link.trim(),
          android_link: form.android_link.trim(),
          ios_link: form.ios_link.trim(),
          video_url: form.video_url.trim(),
          reward_points: rewardPoints,
          visit_duration: visitDuration,
          is_active: Boolean(form.is_active),
          status: form.status,
          weight: form.weight === "" ? 100 : Number(form.weight),
          cooldown_days: cooldownDays,
          valid_from: form.valid_from || null,
          valid_until: form.valid_until || null,
          max_completions: maxCompletions,
        };
      }

      await onSave(payload, editingTask?.id, isMultipart);
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to save task");
    } finally {
      setLoading(false);
    }
  };

  // A plain-English reading of the form as it stands. The individual fields
  // each make sense on their own; what an admin cannot easily do is hold all
  // of them in their head at once and picture the result — so the form says
  // it back to them.
  const platformText =
    form.platform === "both" ? "Android and iOS" : form.platform === "ios" ? "iOS only" : "Android only";

  const countryNames = form.countries.map(countryName);
  const audienceText =
    countryNames.length === 0
      ? "in every country"
      : countryNames.length <= 3
      ? `for users in ${countryNames.join(", ")}`
      : `for users in ${countryNames.slice(0, 3).join(", ")} and ${countryNames.length - 3} more`;

  const rewardText =
    Number(form.reward_points) > 0
      ? `pays ${form.reward_points} event points`
      : "pays nothing";

  const repeatText =
    form.cooldown_days === "" || form.cooldown_days === null
      ? "runs once and never repeats"
      : `can run again every ${form.cooldown_days} days`;

  const windowText =
    form.valid_from || form.valid_until
      ? `, between ${form.valid_from || "now"} and ${form.valid_until || "no end date"}`
      : "";

  const capText = form.max_completions ? `, up to ${form.max_completions} completions` : "";

  // The two ways a saved task quietly never reaches anyone.
  const willNotPublish = form.status !== "ready" || !form.is_active;

  // On `both`, a platform with no link of its own falls back to the main one —
  // and if that is empty too, that platform gets nothing.
  const missingFallback =
    form.platform === "both" &&
    !form.link.trim() &&
    (!form.android_link.trim() || !form.ios_link.trim());

  const showVideo = form.task_type === "youtube" || Boolean(form.video_url);

  // Which lifecycle button is lit. `ready` only counts as Live while the task
  // is also active — the one combination where the two stored fields disagree.
  const lifecycle =
    form.status === "archived"
      ? "archived"
      : form.status === "draft"
      ? "draft"
      : form.status === "ready" && form.is_active
      ? "live"
      : "paused";

  const setLifecycle = (values) => setForm({ ...form, ...values });

  const repeatValue = form.cooldown_days === null ? "" : String(form.cooldown_days);
  const isPresetRepeat = REPEAT_PRESETS.some((p) => String(p.value) === repeatValue);

  // A task carrying a cap or a date window has had its advanced settings used
  // deliberately — hiding them on open would look like they had been lost.
  const hasAdvanced = Boolean(form.max_completions || form.valid_from || form.valid_until);

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">{isEditing ? "Edit Task" : "Add New Task"}</h2>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Four steps: what it is, where it sends people, what it pays, and when it runs.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
          >
            <FiX size={17} />
          </button>
        </div>

        <form onSubmit={handleSubmit} onPaste={handlePaste} className="flex-1 overflow-y-auto p-6 space-y-1">
          <Section
            step="1"
            title="What the task is"
            hint="The title and image are what the user sees on the Tasks screen."
          >
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Event <span className="text-red-500">*</span>
              </label>
              <select
                aria-label="Event"
                value={form.event_id}
                onChange={(e) => setForm({ ...form, event_id: e.target.value })}
                disabled={eventsState === "loading"}
                className={`${FIELD_CLASS} disabled:bg-gray-50 disabled:text-gray-400`}
              >
                {/* The empty option is never an event. While editing it means
                    "belongs to none"; while creating it is only offered when
                    there is nothing running to preselect. */}
                {(eventsState !== "ready" || isEditing || !runningEvent) && (
                  <option value="">
                    {eventsState === "loading"
                      ? "Loading events..."
                      : isEditing
                      ? "No event — hidden from the app"
                      : "Choose an event"}
                  </option>
                )}
                {events.map((event) => (
                  <option key={event.id} value={String(event.id)}>
                    {event.name}
                    {event.status === "running" ? " — running now" : ""}
                    {event.status === "coming_soon" ? " — coming soon" : ""}
                    {event.status === "completed" ? " — finished" : ""}
                  </option>
                ))}
              </select>

              {eventsState === "failed" && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-1.5">
                  <p className="font-semibold">The event list could not be loaded.</p>
                  {eventsError && <p className="mt-0.5">{eventsError}</p>}
                  <p className="mt-0.5">
                    Saving without choosing one still files the task under the event running now.{" "}
                    <button
                      type="button"
                      onClick={() => setEventsAttempt((n) => n + 1)}
                      className="font-semibold underline"
                    >
                      Try again
                    </button>
                  </p>
                </div>
              )}

              {eventsState === "ready" && events.length === 0 && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-1.5">
                  There are no events yet. Create one under Events List first — a task with no event never
                  appears in the app.
                </p>
              )}

              {eventsState === "ready" && events.length > 0 && (
                <p className="text-[11px] text-gray-400 mt-1">
                  {runningEvent && String(runningEvent.id) === form.event_id
                    ? "This event is live, so the task appears in the app as soon as it is saved."
                    : form.event_id === ""
                    ? "A task with no event never appears in the app."
                    : "Users see every task in the running event, and nothing from any other — this one waits until its event starts."}
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Visit our news partner"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className={FIELD_CLASS}
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Task Type <span className="text-red-500">*</span>
                </label>
                <select
                  aria-label="Task Type"
                  value={form.task_type}
                  onChange={(e) => handleTaskTypeChange(e.target.value)}
                  className={FIELD_CLASS}
                >
                  {TASK_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-400 mt-1">Sets the icon and the default wording below.</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Platform</label>
                <select
                  aria-label="Platform"
                  value={form.platform}
                  onChange={(e) => setForm({ ...form, platform: e.target.value })}
                  className={FIELD_CLASS}
                >
                  {TASK_PLATFORMS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-400 mt-1">Which app this task shows up on</p>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Countries <span className="text-gray-300">(optional — empty = global)</span>
              </label>
              <CountryPicker
                value={form.countries}
                onChange={(countries) => setForm((prev) => ({ ...prev, countries }))}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea
                rows={3}
                placeholder="What the user should do to complete this task"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className={`${FIELD_CLASS} resize-none`}
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Filled in from the task type — edit it freely. Once you change the wording it stays as you
                wrote it, even if you switch type.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Image</label>
              <input
                type="file"
                ref={fileInputRef}
                accept={TASK_IMAGE_ACCEPT}
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`w-full border-2 border-dashed p-3 rounded-xl flex flex-col items-center justify-center gap-1 transition text-gray-500 hover:text-blue-600 bg-gray-50/50 ${
                  dragOver ? "border-blue-400 bg-blue-50/50" : "border-gray-200 hover:border-blue-400"
                }`}
              >
                <FiImage size={18} />
                <span className="text-xs font-medium">Click to upload, drag &amp; drop, or paste (Ctrl+V)</span>
                <span className="text-[11px] text-gray-400">{TASK_IMAGE_HINT}</span>
              </button>

              {previewUrl && (
                <div className="mt-3 flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                  <img
                    src={previewUrl}
                    alt="Task preview"
                    className="w-12 h-12 object-contain rounded-lg bg-white border border-gray-200 p-1"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <div className="text-xs text-gray-500 min-w-0">
                    <p className="font-medium text-gray-700">Image Preview</p>
                    <p className="text-[11px] text-gray-400 truncate max-w-xs">
                      {imageFile ? imageFile.name : previewUrl}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Section>

          <Section
            step="2"
            title="Where it sends the user"
            hint="Tapping the task opens this link. Get it wrong and the task is dead on arrival."
          >
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {LINK_LABEL[form.task_type] || "Link"}
                {form.platform === "both" ? " (fallback)" : ""}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder={LINK_PLACEHOLDER[form.task_type] || "https://example.com"}
                  value={form.link}
                  onChange={(e) => setForm({ ...form, link: e.target.value })}
                  className={FIELD_CLASS}
                />
                {form.task_type === "website_visit" && (
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    title="List this site pages and pick one"
                    className="px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition flex items-center gap-1.5 shrink-0"
                  >
                    <FiGlobe size={14} /> Browse
                  </button>
                )}
              </div>
              {form.task_type === "website_visit" && (
                <p className="text-[11px] text-gray-400 mt-1">
                  Browse reads the site own page list, so you can pick the page instead of pasting its URL.
                </p>
              )}
              {form.platform === "both" && (
                <p className="text-[11px] text-gray-400 mt-1">
                  Used for whichever platform has no link of its own below.
                </p>
              )}
            </div>

            {form.platform === "both" && (
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Android Link</label>
                    <input
                      type="text"
                      placeholder="Play Store URL"
                      value={form.android_link}
                      onChange={(e) => setForm({ ...form, android_link: e.target.value })}
                      className={FIELD_CLASS}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">iOS Link</label>
                    <input
                      type="text"
                      placeholder="App Store URL"
                      value={form.ios_link}
                      onChange={(e) => setForm({ ...form, ios_link: e.target.value })}
                      className={FIELD_CLASS}
                    />
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">
                  Only needed when the destination differs per store. The app sends its own platform, so each
                  user gets the right one.
                </p>
                {missingFallback && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-2">
                    One platform has no link and there is no fallback — those users will tap the task and go
                    nowhere.
                  </p>
                )}
              </div>
            )}

            {showVideo && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Video URL</label>
                <input
                  type="text"
                  placeholder="YouTube video URL"
                  value={form.video_url}
                  onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                  className={FIELD_CLASS}
                />
                <p className="text-[11px] text-gray-400 mt-1">Played in-app, where the link opens outside it.</p>
              </div>
            )}
          </Section>

          <Section
            step="3"
            title="What it pays, and what counts as done"
            hint="Points are event points — they only land while an event is running."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reward (Event Points)</label>
                <input
                  type="number"
                  min={0}
                  placeholder="0"
                  value={form.reward_points}
                  onChange={(e) => setForm({ ...form, reward_points: e.target.value })}
                  className={FIELD_CLASS}
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  {Number(form.reward_points) > 0
                    ? "Locked in when the task is published — later edits do not change what was already earned."
                    : "Zero is allowed: a follow or join with no reward."}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Visit Duration (seconds)</label>
                <input
                  type="number"
                  min={1}
                  placeholder="e.g. 30 — leave blank if not required"
                  value={form.visit_duration}
                  onChange={(e) => setForm({ ...form, visit_duration: e.target.value })}
                  className={FIELD_CLASS}
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  How long the link must stay open before the app lets them mark it complete.
                </p>
              </div>
            </div>
          </Section>

          <Section
            step="4"
            title="When it runs"
            hint="Tasks go into a pool, and each morning a few of them are published to the app. These two answers decide how often this one is picked."
          >
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Is it ready to go out?
              </label>
              <div className="flex flex-wrap gap-2">
                {LIFECYCLE.filter((option) => option.key !== "archived" || isEditing).map((option) => (
                  <Choice
                    key={option.key}
                    selected={lifecycle === option.key}
                    label={option.label}
                    hint={option.hint}
                    onClick={() => setLifecycle(option.values)}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                How often can it come back?
              </label>
              <div className="flex flex-wrap gap-2">
                {REPEAT_PRESETS.map((preset) => (
                  <Choice
                    key={preset.label}
                    selected={String(preset.value) === repeatValue}
                    label={preset.label}
                    onClick={() => setForm({ ...form, cooldown_days: preset.value })}
                  />
                ))}
                <Choice
                  selected={!isPresetRepeat}
                  label="Custom"
                  onClick={() => setForm({ ...form, cooldown_days: 14 })}
                />
              </div>

              {!isPresetRepeat && (
                <div className="flex items-center gap-2 mt-2.5">
                  <span className="text-xs text-gray-500">Every</span>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={form.cooldown_days}
                    onChange={(e) => setForm({ ...form, cooldown_days: e.target.value })}
                    placeholder="Never"
                    className={`${FIELD_CLASS} w-24`}
                  />
                  <span className="text-xs text-gray-500">days</span>
                </div>
              )}

              <p className="text-[11px] text-gray-400 mt-1.5">
                {repeatValue === ""
                  ? "Left empty, this task runs once and never again."
                  : `Can come back up every ${form.cooldown_days} days.`}
              </p>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced((open) => !open)}
                className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 transition"
              >
                {showAdvanced ? "Hide" : "Show"} limits and dates
                {!showAdvanced && hasAdvanced ? " (in use)" : ""}
              </button>

              {showAdvanced && (
                <div className="mt-3 space-y-4 p-4 rounded-xl bg-gray-50/70 border border-gray-100">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      How often should it be picked, against everything else?
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {PRIORITIES.map((option) => (
                        <Choice
                          key={option.label}
                          selected={Number(form.weight) === option.value}
                          label={option.label}
                          hint={option.hint}
                          onClick={() => setForm({ ...form, weight: option.value })}
                        />
                      ))}
                    </div>
                    {!PRIORITIES.some((option) => option.value === Number(form.weight)) && (
                      <p className="text-[11px] text-gray-400 mt-1.5">
                        Currently set to {form.weight} — pick one above to change it.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Stop after this many people finish it
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={form.max_completions}
                      onChange={(e) => setForm({ ...form, max_completions: e.target.value })}
                      placeholder="No limit"
                      className={`${FIELD_CLASS} sm:w-52`}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Only run between these dates
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="date"
                        value={form.valid_from}
                        onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                        className={`${FIELD_CLASS} sm:w-44`}
                      />
                      <span className="text-xs text-gray-500">to</span>
                      <input
                        type="date"
                        value={form.valid_until}
                        onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                        className={`${FIELD_CLASS} sm:w-44`}
                      />
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1.5">
                      Leave blank to run indefinitely. A campaign with an end date switches itself off —
                      nobody has to remember.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Section>

          {/* The form, said back in one sentence. */}
          <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50/50 p-4">
            <p className="text-[11px] font-bold text-blue-900 uppercase tracking-wider mb-1.5">In plain words</p>
            <p className="text-xs text-blue-900 leading-relaxed">
              On <span className="font-semibold">{platformText}</span>{" "}
              <span className="font-semibold">{audienceText}</span>, this task {rewardText} and {repeatText}
              {windowText}
              {capText}.
            </p>
            <p className="text-[11px] text-blue-800/80 mt-1.5">
              {lifecycle === "live"
                ? "It is in the pool now, so the next daily build can pick it."
                : "It is not in the pool, so no daily build will pick it."}
            </p>
            {willNotPublish && (
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-2">
                {!form.is_active
                  ? "It is switched off, so nobody will see it until you activate it."
                  : "Its pool status is not Ready, so the scheduler will not publish it."}
              </p>
            )}
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm shadow-blue-600/20 transition flex items-center gap-2"
          >
            {loading && (
              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isEditing ? "Save Changes" : "Add Task"}
          </button>
        </div>
      </div>
    </div>

    <PagePickerModal
      isOpen={pickerOpen}
      initialUrl={form.link}
      onClose={() => setPickerOpen(false)}
      onPick={(url) => setForm({ ...form, link: url })}
    />
    </>
  );
};

export default TaskModal;
